import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';
import z from 'zod';

import { authorizedAxiosInstance } from '@/clients';

const MARKET_HOURS_STALE_TIME = 30_000;
const MARKET_HOURS_REFRESH_INTERVAL = 60_000;
const MARKET_HOLIDAYS_STALE_TIME = 12 * 60 * 60 * 1_000;

export const fmpExchangeMarketHourSchema = z
    .object({
        closingAdditional: z.string().trim().min(1).optional(),
        closingHour: z.string().trim().min(1),
        exchange: z.string().trim().min(1),
        isMarketOpen: z.boolean(),
        name: z.string().trim().min(1),
        openingAdditional: z.string().trim().min(1).optional(),
        openingHour: z.string().trim().min(1),
        timezone: z.string().trim().min(1),
    })
    .strict();

const fmpExchangeMarketHoursSchema = z.array(fmpExchangeMarketHourSchema);

export const fmpExchangeMarketHolidaySchema = z
    .object({
        adjCloseTime: z.string().trim().min(1).nullable(),
        adjOpenTime: z.string().trim().min(1).nullable(),
        date: z.string().date(),
        exchange: z.string().trim().min(1),
        isClosed: z.boolean(),
        name: z.string().trim().min(1),
    })
    .strict();

const fmpExchangeMarketHolidaysSchema = z.array(fmpExchangeMarketHolidaySchema);

export type FmpExchangeMarketHour = z.infer<typeof fmpExchangeMarketHourSchema>;
export type FmpExchangeMarketHoliday = z.infer<typeof fmpExchangeMarketHolidaySchema>;

export const parseFmpExchangeMarketHours = (response: unknown) => fmpExchangeMarketHoursSchema.parse(response);
export const parseFmpExchangeMarketHolidays = (response: unknown) => fmpExchangeMarketHolidaysSchema.parse(response);

const normalizeMarketExchanges = (exchanges: readonly string[]) =>
    Array.from(new Set(exchanges.map((exchange) => exchange.trim().toUpperCase()).filter(Boolean))).sort();

const fetchMarketHours = async (signal: AbortSignal) => {
    try {
        const response = await authorizedAxiosInstance.get('/api/vendors/fmp/trading-hours', { signal });

        return parseFmpExchangeMarketHours(response.data);
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

const fetchMarketHolidays = async (exchanges: readonly string[], signal: AbortSignal) => {
    if (exchanges.length === 0) {return [];}

    try {
        const response = await authorizedAxiosInstance.get('/api/vendors/fmp/trading-hours/holidays', {
            params: { exchanges: exchanges.join(',') },
            signal,
        });

        return parseFmpExchangeMarketHolidays(response.data);
    } catch (error) {
        if (!signal.aborted) {
            captureException(error);
        }

        throw error;
    }
};

export const marketHoursQueryKeys = {
    all: ['vendors', 'fmp', 'market-hours', 'all'] as const,
    holidays: (exchanges: readonly string[]) =>
        ['vendors', 'fmp', 'market-hours', 'holidays', ...normalizeMarketExchanges(exchanges)] as const,
};

export const marketHoursQueryOptions = {
    all: () =>
        queryOptions({
            queryFn: ({ signal }) => fetchMarketHours(signal),
            queryKey: marketHoursQueryKeys.all,
            refetchInterval: MARKET_HOURS_REFRESH_INTERVAL,
            retry: 1,
            staleTime: MARKET_HOURS_STALE_TIME,
        }),
    holidays: (exchanges: readonly string[], enabled = true) => {
        const normalizedExchanges = normalizeMarketExchanges(exchanges);

        return queryOptions({
            enabled: enabled && normalizedExchanges.length !== 0,
            gcTime: 24 * 60 * 60 * 1_000,
            queryFn: ({ signal }) => fetchMarketHolidays(normalizedExchanges, signal),
            queryKey: marketHoursQueryKeys.holidays(normalizedExchanges),
            retry: 1,
            staleTime: MARKET_HOLIDAYS_STALE_TIME,
        });
    },
};
